import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogHeader,
    Select,
    SelectOption,
    ToastLevel,
    useIntl,
    useToast,
} from "@metsooutotec/modes-react-components"
import { convertValueToOption } from "components/common/select-component-helper"
import usersStore from "features/users/store/users-store"
import { observer } from "mobx-react-lite"
import { FC, useState } from "react"

interface AssignUsersToCustomerModalProps {
    id: string
    isOpen: boolean
    setIsOpen: (open: boolean) => void
    type: "customer" | "site"
}

const AssignUsersModal: FC<AssignUsersToCustomerModalProps> = observer(({ id, isOpen, setIsOpen, type }) => {
    const { formatMessage } = useIntl()
    const { setMessage } = useToast()
    const { users, userLoading } = usersStore

    const assignableUsers = users.filter(user => {
        if (!user.permissions) return true
        return !user.permissions.some(p => (type === "customer" ? p.customerId === id && !p.siteId : p.siteId === id))
    })

    const [selectedUserId, setSelectedUserId] = useState<string>()

    const usersOptions: SelectOption[] = assignableUsers.map(user => ({
        text: user.displayName || user.email || "Unknown",
        value: user.id,
    }))

    const handleSave = async () => {
        try {
            if (!selectedUserId) return

            const result =
                type === "site" ? await usersStore.assignUserToSite(selectedUserId, id) : await usersStore.assignUserToCustomer(selectedUserId, id)

            if (!result.passed) {
                setMessage(formatMessage("userAssign.error"), ToastLevel.ALARM)
                return
            }
            setMessage(formatMessage("userAssign.success"), ToastLevel.SUCCESS)
            setIsOpen(false)
        } catch (error) {
            console.error(error)
            setMessage(formatMessage("userAssign.error"), ToastLevel.ALARM)
        }
    }

    const onUserChange = (picked: SelectOption) => {
        if (picked?.value) setSelectedUserId(picked.value)
    }

    return (
        <Dialog open={isOpen} onClose={() => setIsOpen(false)} size='tiny'>
            <DialogHeader>{type === "customer" ? formatMessage("userAssign.headerCustomer") : formatMessage("userAssign.headerSite")}</DialogHeader>
            <DialogContent>
                <Select
                    label={formatMessage("userAssign.selectLabel")}
                    options={usersOptions}
                    value={selectedUserId ?? ""}
                    onChange={value => onUserChange(convertValueToOption(value, usersOptions))}
                    required
                />
            </DialogContent>
            <DialogActions>
                <Button children={formatMessage("common.cancel")} onClick={() => setIsOpen(false)} flexible centered />
                <Button
                    children={formatMessage("common.save")}
                    onClick={handleSave}
                    primary
                    disabled={userLoading || !selectedUserId}
                    loading={userLoading}
                    flexible
                    centered
                />
            </DialogActions>
        </Dialog>
    )
})

export default AssignUsersModal
